import { useMemo, useContext, createContext, useCallback, SyntheticEvent, useReducer } from 'react'
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar'
import Alert, { AlertColor } from '@mui/material/Alert'

const SnackbarContext = createContext<{
  showError: (errorMessage: string) => void
  showSuccess: (successMessage: string) => void
} | null>(null)

const useSnackbar = () => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('no SnackbarProvider found when calling useSnackbar()')
  }
  return context
}

export interface SnackbarMessage {
  message: string
  key: number
  severity: AlertColor
}

type State = {
  current?: SnackbarMessage
  queue: SnackbarMessage[]
  open: boolean
}

type Action = { type: 'ENQUEUE'; snack: SnackbarMessage } | { type: 'CLOSE' } | { type: 'EXITED' }

const SnackbarProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    (state: State, action: Action): State => {
      switch (action.type) {
        case 'ENQUEUE': {
          if (!state.current) {
            return { current: action.snack, queue: [], open: true }
          }
          return { ...state, queue: [...state.queue, action.snack], open: false }
        }
        case 'CLOSE': {
          const [, next, ...rest] = state.queue
          if (next) {
            return { current: next, queue: rest, open: true }
          } else {
            return { current: undefined, queue: [], open: false }
          }
        }
        case 'EXITED': {
          const [next, ...rest] = state.queue
          if (next) {
            return { current: next, queue: rest, open: true }
          } else {
            return { current: undefined, queue: [], open: false }
          }
        }
        default:
          return state
      }
    },
    {
      current: undefined,
      queue: [],
      open: false,
    },
  )
  const showError = useCallback((errorMessage: string) => {
    dispatch({
      type: 'ENQUEUE',
      snack: { message: errorMessage, severity: 'error', key: new Date().getTime() },
    })
  }, [])
  const showSuccess = useCallback((successMessage: string) => {
    dispatch({
      type: 'ENQUEUE',
      snack: { message: successMessage, severity: 'success', key: new Date().getTime() },
    })
  }, [])
  const handleClose = useCallback((event: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return
    }
    dispatch({ type: 'CLOSE' })
  }, [])

  const handleExited = useCallback(() => {
    dispatch({ type: 'EXITED' })
  }, [])
  const value = useMemo(
    () => ({
      showError,
      showSuccess,
    }),
    [showError, showSuccess],
  )
  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        key={state.current?.key}
        open={state.open}
        autoHideDuration={3000}
        onClose={handleClose}
        slotProps={{
          transition: {
            onExited: handleExited,
          },
        }}
      >
        <Alert severity={state.current?.severity} variant="filled">
          {state.current?.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  )
}

export { useSnackbar, SnackbarProvider }
export default SnackbarProvider
